import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Loader2, Plus, Tag, Percent, Clock, Eye, EyeOff, 
  Trash2, Edit, ArrowLeft, Star, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCoupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: 0,
    discount_amount: null,
    max_uses: null,
    valid_from: '',
    valid_until: '',
    is_active: true,
    is_featured: false,
    display_message: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_percent: 0,
      discount_amount: null,
      max_uses: null,
      valid_from: '',
      valid_until: '',
      is_active: true,
      is_featured: false,
      display_message: ''
    });
    setEditingCoupon(null);
  };

  const handleOpenDialog = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        discount_percent: coupon.discount_percent || 0,
        discount_amount: coupon.discount_amount || null,
        max_uses: coupon.max_uses || null,
        valid_from: coupon.valid_from ? coupon.valid_from.slice(0, 16) : '',
        valid_until: coupon.valid_until ? coupon.valid_until.slice(0, 16) : '',
        is_active: coupon.is_active,
        is_featured: coupon.is_featured || false,
        display_message: coupon.display_message || ''
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.code) {
      toast.error('Coupon code is required');
      return;
    }
    if (formData.discount_percent <= 0 && !formData.discount_amount) {
      toast.error('Please set a discount percentage or amount');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        discount_amount: formData.discount_amount || null,
        max_uses: formData.max_uses || null
      };

      let response;
      if (editingCoupon) {
        response = await fetch(`${API_URL}/api/admin/coupons/${editingCoupon.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API_URL}/api/admin/coupons`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingCoupon ? 'Coupon updated!' : 'Coupon created!');
        setShowDialog(false);
        resetForm();
        fetchCoupons();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save coupon');
      }
    } catch (error) {
      toast.error('Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/admin/coupons/${coupon.id}?is_active=${!coupon.is_active}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'}`);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/coupons/${couponId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Coupon deleted');
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="admin-coupons">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Tag className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
                  Coupon <span className="text-primary">Management</span>
                </h1>
                <p className="text-muted-foreground text-sm">
                  Create and manage promotional coupons
                </p>
              </div>
            </div>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-primary hover:bg-primary/90"
              data-testid="create-coupon-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <Card className="bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Coupons</p>
              <p className="text-2xl font-bold">{coupons.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold text-green-500">
                {coupons.filter(c => c.is_active && !isExpired(c.valid_until)).length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Featured</p>
              <p className="text-2xl font-bold text-yellow-500">
                {coupons.filter(c => c.is_featured && c.is_active).length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Uses</p>
              <p className="text-2xl font-bold text-primary">
                {coupons.reduce((sum, c) => sum + (c.times_used || 0), 0)}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Coupons Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="font-heading uppercase">All Coupons</CardTitle>
            </CardHeader>
            <CardContent>
              {coupons.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No coupons yet. Create your first coupon!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Discount</TableHead>
                        <TableHead>Uses</TableHead>
                        <TableHead>Valid Until</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Featured</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coupons.map((coupon) => (
                        <TableRow key={coupon.id} data-testid={`coupon-row-${coupon.code}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="font-mono font-bold text-primary">
                                {coupon.code}
                              </code>
                              {coupon.display_message && (
                                <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                                  "{coupon.display_message}"
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                              {coupon.discount_percent > 0 
                                ? `${coupon.discount_percent}%` 
                                : `$${coupon.discount_amount}`
                              }
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono">
                              {coupon.times_used || 0}
                              {coupon.max_uses && `/${coupon.max_uses}`}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {coupon.valid_until ? (
                                <>
                                  <Clock className={`w-3 h-3 ${isExpired(coupon.valid_until) ? 'text-red-400' : 'text-muted-foreground'}`} />
                                  <span className={isExpired(coupon.valid_until) ? 'text-red-400' : ''}>
                                    {formatDate(coupon.valid_until)}
                                  </span>
                                </>
                              ) : (
                                <span className="text-muted-foreground">No expiry</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {coupon.is_active && !isExpired(coupon.valid_until) ? (
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                <Eye className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            ) : isExpired(coupon.valid_until) ? (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                Expired
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                                <EyeOff className="w-3 h-3 mr-1" />
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {coupon.is_featured ? (
                              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                            ) : (
                              <Star className="w-5 h-5 text-gray-600" />
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(coupon)}
                                data-testid={`toggle-${coupon.code}`}
                              >
                                {coupon.is_active ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenDialog(coupon)}
                                data-testid={`edit-${coupon.code}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => handleDelete(coupon.id)}
                                data-testid={`delete-${coupon.code}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg bg-card">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Coupon Code */}
              <div>
                <Label htmlFor="code">Coupon Code *</Label>
                <Input
                  id="code"
                  placeholder="e.g., WELCOME50"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="font-mono uppercase"
                  data-testid="input-code"
                />
              </div>

              {/* Discount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="discount_percent">Discount % *</Label>
                  <div className="relative">
                    <Input
                      id="discount_percent"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({...formData, discount_percent: parseInt(e.target.value) || 0})}
                      className="pr-8"
                      data-testid="input-percent"
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="discount_amount">Or Fixed Amount ($)</Label>
                  <Input
                    id="discount_amount"
                    type="number"
                    min="0"
                    placeholder="Optional"
                    value={formData.discount_amount || ''}
                    onChange={(e) => setFormData({...formData, discount_amount: parseFloat(e.target.value) || null})}
                    data-testid="input-amount"
                  />
                </div>
              </div>

              {/* Display Message */}
              <div>
                <Label htmlFor="display_message">Widget Display Message</Label>
                <Input
                  id="display_message"
                  placeholder="e.g., Limited Time Offer! 50% OFF!"
                  value={formData.display_message}
                  onChange={(e) => setFormData({...formData, display_message: e.target.value})}
                  data-testid="input-message"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Shown in the floating coupon widget
                </p>
              </div>

              {/* Max Uses */}
              <div>
                <Label htmlFor="max_uses">Max Uses (leave empty for unlimited)</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={formData.max_uses || ''}
                  onChange={(e) => setFormData({...formData, max_uses: parseInt(e.target.value) || null})}
                  data-testid="input-max-uses"
                />
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valid_from">Valid From</Label>
                  <Input
                    id="valid_from"
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                    data-testid="input-valid-from"
                  />
                </div>
                <div>
                  <Label htmlFor="valid_until">Valid Until</Label>
                  <Input
                    id="valid_until"
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    data-testid="input-valid-until"
                  />
                </div>
              </div>

              {/* Toggles */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                    data-testid="switch-active"
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
                    data-testid="switch-featured"
                  />
                  <Label className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-400" />
                    Featured (show in widget)
                  </Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-coupon-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminCoupons;
